// src/pages/Wallet/modals/NFTInfoModal.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import './NFTInfoModal.css';

const NFTInfoModal = ({ isOpen, onClose, nft, onSend }) => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  const [imageError, setImageError] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [copied, setCopied] = useState(false);
  
  if (!isOpen || !nft || !nft.nft) return null;
  
  const { nft: nftData } = nft;
  
  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };
  
  // Handle Send button click
  const handleSend = () => {
    if (onSend) {
      onClose(); // Close the modal first
      onSend(nft); // Call the parent's onSend function with the NFT data
    }
  };

  // Copy address to clipboard
  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(nftData.contractAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Get display name for the NFT
  const getDisplayName = () => {
    if (nftData.name) {
      return nftData.name;
    }
    if (nftData.collectionName) {
      return `${nftData.collectionName} #${nftData.tokenId}`;
    }
    return `Token #${nftData.tokenId}`;
  };
  
  // Get collection name
  const getCollectionName = () => {
    return nftData.collectionName || t('wallet.nft.unknownCollection', 'Unknown Collection');
  };
  
  // Get image source
  const getImageSrc = () => {
    if (imageError) return null;
    return nftData.imageUrl || nftData.image;
  };
  
  // Get explorer URL for the NFT
  const getExplorerUrl = () => {
    if (!currentChain?.explorerUrl) return null;
    return `${currentChain.explorerUrl}/token/${nftData.contractAddress}?a=${nftData.tokenId}`;
  };
  
  // Truncate description
  const getTruncatedDescription = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  // Format attributes for display
  const formatAttributes = () => {
    if (!nftData.attributes || !Array.isArray(nftData.attributes)) {
      return [];
    }
    
    return nftData.attributes.filter(attr => 
      attr && typeof attr === 'object' && (attr.trait_type || attr.value)
    );
  };
  
  return (
    <div className="nft-info-modal-overlay">
      <div className="nft-info-modal">
        <div className="nft-modal-header">
          <h2 className="nft-modal-title">{getDisplayName()}</h2>
          <button className="nft-modal-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="nft-modal-content">
          {/* NFT Image Section */}
          <div className="nft-modal-image-section">
            {imageError || !getImageSrc() ? (
              <div className="nft-modal-image-fallback">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
                <span>{t('wallet.nft.noImageAvailable', 'No Image Available')}</span>
              </div>
            ) : (
              <img
                src={getImageSrc()}
                alt={getDisplayName()}
                className="nft-modal-image"
                onError={handleImageError}
              />
            )}
          </div>
          
          {/* NFT Details Section */}
          <div className="nft-modal-details">
            <div className="nft-detail-group">
              <h3 className="nft-detail-label">{t('wallet.nft.collection', 'Collection')}</h3>
              <p className="nft-detail-value">{getCollectionName()}</p>
            </div>
            
            <div className="nft-detail-group">
              <h3 className="nft-detail-label">{t('wallet.nft.tokenId', 'Token ID')}</h3>
              <p className="nft-detail-value">
                #{nftData.tokenId}
                {nftData.virtualTokenId && (
                  <span className="virtual-token-note">
                    {' '}({t('wallet.nft.virtualId', 'Virtual ID')})
                  </span>
                )}
              </p>
            </div>
            
            <div className="nft-detail-group">
              <h3 className="nft-detail-label">{t('wallet.nft.contractAddress', 'Contract Address')}</h3>
              <div className="nft-detail-value contract-address">
                {nftData.contractAddress}
                <button 
                  className="copy-address-btn" 
                  onClick={handleCopyAddress}
                  title={t('actions.copyAddress', 'Copy address')}
                >
                  {copied ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            {nftData.description && (
              <div className="nft-detail-group">
                <h3 className="nft-detail-label">{t('wallet.nft.description', 'Description')}</h3>
                <p className="nft-detail-value description">
                  {showFullDescription 
                    ? nftData.description 
                    : getTruncatedDescription(nftData.description)
                  }
                  {nftData.description.length > 150 && (
                    <button 
                      className="description-toggle"
                      onClick={() => setShowFullDescription(!showFullDescription)}
                    >
                      {showFullDescription 
                        ? t('wallet.nft.showLess', 'Show Less')
                        : t('wallet.nft.showMore', 'Show More')
                      }
                    </button>
                  )}
                </p>
              </div>
            )}
            
            {/* Attributes Section */}
            {formatAttributes().length > 0 && (
              <div className="nft-detail-group">
                <h3 className="nft-detail-label">{t('wallet.nft.attributes', 'Attributes')}</h3>
                <div className="nft-attributes">
                  {formatAttributes().map((attr, index) => (
                    <div key={index} className="nft-attribute">
                      <span className="attribute-trait">
                        {attr.trait_type || 'Property'}
                      </span>
                      <span className="attribute-value">
                        {attr.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="nft-modal-actions">
              {/* Send Button */}
              <button
                className="nft-action-btn send-btn-nft"
                onClick={handleSend}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22,2 15,22 11,13 2,9"></polygon>
                </svg>
                {t('actions.send', 'Send')}
              </button>

              {/* Explorer Link */}
              {getExplorerUrl() && (
                <a
                  href={getExplorerUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nft-action-btn explorer-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15,3 21,3 21,9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  {t('wallet.nft.viewOnExplorer', 'View on Explorer')}
                </a>
              )}
              
              {/* External Link */}
              {nftData.externalUrl && (
                <a
                  href={nftData.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nft-action-btn external-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15,3 21,3 21,9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  {t('wallet.nft.viewExternal', 'View External')}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFTInfoModal;